import {
    ColoursObj,
    Gutters,
    MainButton,
    MobileConfirmDialog,
    TDialogStore,
    useDialogStore,
} from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { bs, useBoundStore } from '../../../state/bound.store'
import { selectCurrentItineraryTitle } from '../../../state/itineraryState/currentItin.slice'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'
import { NavigationView } from '../../layout/NavigationView'
import { ToolbarItemProps } from '../../layout/TopToolbar'
interface IEditItemProps {
    children: React.ReactNode
    deleteButtonText: string
    headerText: string
    isNewItem?: boolean
    saveAllowed?: boolean
}
export const BaseEditItem = ({
    children,
    deleteButtonText,
    headerText,
    isNewItem = false,
    saveAllowed = true,
}: IEditItemProps) => {
    const { formState, handleSubmit } = useFormContext()
    const { isDirty } = formState
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )
    const itineraryTitle = useBoundStore(selectCurrentItineraryTitle)
    const { idItem } = useParams<{
        idItem: string
    }>()

    const onDelete = async () => {
        try {
            loadingSignal.value = true
            await bs().deleteElement(8, idItem)
            window.goBack()
            clearDialog()
        } catch (error) {
            defaultAddSnack(
                i18n.t('edit-itinerary.item.delete-booking-error'),
                'error'
            )
        } finally {
            loadingSignal.value = false
        }
    }

    const onDeleteClick = () => {
        replaceDialog({
            node: (
                <MobileConfirmDialog
                    text={i18n.t('edit-itinerary.item.delete-booking-text')}
                    header={i18n.t('edit-itinerary.item.delete-booking')}
                    onConfirm={onDelete}
                    onCancel={() => {
                        clearDialog()
                    }}
                    cancelText={i18n.t('general.cancel')}
                    confirmText={i18n.t('general.delete')}
                />
            ),
            backdropClosable: false,
        })
    }
    const onSaveData = async (id: string) => {
        try {
            await handleSubmit(async (data) => {
                try {
                    loadingSignal.value = true
                    const res = await bs().upsertElement(8, data)
                    if (res.status === 'success') {
                        window.goBack()
                    }
                } catch (error) {
                } finally {
                    loadingSignal.value = false
                }
            })()
        } catch (error) {
            // Handle error
        }
    }
    const handleDialogConfirm = useCallback(() => {
        clearDialog()

        window.goBack()
    }, [clearDialog])
    const onBackPressed = useCallback(() => {
        if (isDirty) {
            replaceDialog({
                node: (
                    <MobileConfirmDialog
                        text={i18n.t(
                            'edit-itinerary.item.unsaved-booking-text'
                        )}
                        header={i18n.t('edit-itinerary.item.unsaved-booking')}
                        onCancel={() => {
                            clearDialog()
                        }}
                        onConfirm={handleDialogConfirm}
                        cancelText={i18n.t('general.cancel')}
                        confirmText={i18n.t('general.leave')}
                    />
                ),
                backdropClosable: false,
            })
        } else {
            window.goBack()
        }
    }, [isDirty, replaceDialog, clearDialog, handleDialogConfirm])

    const backButton: ToolbarItemProps = {
        id: 'backButtonNavigation',
        buttonText: i18n.t('general.cancel'),
        useButton: true,
        colour: ColoursObj.core.primary,
        action: () => {
            onBackPressed()
        },
    }
    return (
        <>
            <NavigationView
                usePageTransition
                navigationBarTitle={headerText}
                navigationBarSubtitle={itineraryTitle}
                backButton={backButton}
                backButtonAction={onBackPressed}
                navigationBarTrailing={[
                    {
                        id: 'saveButton',
                        disabled: !saveAllowed,
                        buttonText: i18n.t('general.done'),
                        colour: ColoursObj.core.primary,
                        useButton: true,
                        action: () => {
                            if (idItem) onSaveData(idItem)
                        },
                    },
                ]}
            >
                <Gutters>
                    <Stack gap="16px">
                        <Stack spacing="16px">{children}</Stack>
                        {!isNewItem ? (
                            <Stack
                                gap="16px"
                                direction="row"
                                justifyContent="center"
                            >
                                <MainButton
                                    id="deleteItemButton"
                                    style={{ width: '100%' }}
                                    text={deleteButtonText}
                                    variant="destructiveBorderless"
                                    onClick={onDeleteClick}
                                />
                            </Stack>
                        ) : (
                            <Box height={'16px'} />
                        )}
                    </Stack>
                </Gutters>
            </NavigationView>
        </>
    )
}
